// client/src/pages/Stories.js

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import storyService from '../services/storyService.js';

/* ─── Vintage Search Bar ───────────────────────────── */
function VintageSearch({ value, onChange, placeholder }) {
  return (
    <div className="relative group">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold-dark dark:text-gold text-sm opacity-60 font-display pointer-events-none">✦</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-10 py-3 rounded-xl
          bg-white dark:bg-royal-800/60
          border-2 border-gold/30 dark:border-gold/20
          text-royal-900 dark:text-royal-100
          placeholder:text-royal-400 dark:placeholder:text-royal-300
          placeholder:italic placeholder:font-body
          font-body text-base
          focus:outline-none focus:border-gold/60 dark:focus:border-gold/50
          focus:shadow-lg focus:shadow-gold/5
          transition-all"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        <svg className="w-5 h-5 text-gold-dark dark:text-gold opacity-50 group-focus-within:opacity-80 transition-opacity" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="7" />
          <path strokeLinecap="round" d="M16.5 16.5L21 21" />
        </svg>
      </div>
      <span className="absolute right-10 top-1/2 -translate-y-1/2 text-gold-dark dark:text-gold text-sm opacity-60 font-display pointer-events-none">✦</span>
    </div>
  );
}

export default function Stories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const activeCategory = searchParams.get('category') || '';
  const activeSubcategory = searchParams.get('subcategory') || '';

  // Load categories
  useEffect(() => {
    storyService.getCategories().then((d) => {
      const cats = d.categories || [];
      setCategories(cats);
      setInitialized(true);
    }).catch(() => setInitialized(true));
  }, []);

  // Default to Classics once categories are loaded and no category is in URL
  useEffect(() => {
    if (initialized && !activeCategory && categories.length > 0) {
      const classics = categories.find((c) => c.categoryname === 'Classics');
      if (classics) {
        setSearchParams({ category: String(classics.categoryid) }, { replace: true });
      }
    }
  }, [initialized, categories, activeCategory]);

  // Load subcategories when category changes
  useEffect(() => {
    if (activeCategory) {
      storyService.getSubcategories(activeCategory)
        .then((d) => setSubcategories(d.subcategories || []))
        .catch(() => setSubcategories([]));
    } else {
      setSubcategories([]);
    }
  }, [activeCategory]);

  // Load stories
  useEffect(() => {
    if (!initialized) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (activeCategory) params.set('category', activeCategory);
    if (activeSubcategory) params.set('subcategory', activeSubcategory);
    storyService.getStories(params.toString())
      .then((d) => setStories(d.stories || []))
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, [activeCategory, activeSubcategory, initialized]);

  const selectCategory = (catId) => {
    if (catId) {
      setSearchParams({ category: catId });
    } else {
      setSearchParams({});
    }
    setSearchQuery('');
  };

  // Build story detail URL that preserves current category/subcategory context
  const storyUrl = (slug) => {
    const params = new URLSearchParams();
    if (activeCategory) params.set('from_category', activeCategory);
    if (activeSubcategory) params.set('from_subcategory', activeSubcategory);
    const qs = params.toString();
    return `/stories/${slug}${qs ? `?${qs}` : ''}`;
  };

  // Find category objects
  const classicsCategory = categories.find((c) => c.categoryname === 'Classics');
  const cherryCategory = categories.find((c) => c.categoryname === 'Cherry Fairy Chronicles');
  const activeCategoryName = categories.find((c) => String(c.categoryid) === activeCategory)?.categoryname || '';

  // Filter stories by search query
  const filteredStories = searchQuery
    ? stories.filter((s) =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.author && s.author.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (s.excerpt && s.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : stories;

  return (
    <div className="min-h-screen">

      {/* ── Hero — ALWAYS dark ─────────────────────────── */}
      <div className="relative overflow-hidden bg-royal-900">
        <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(244,208,63,0.05) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-3xl mx-auto px-4 pt-12 sm:pt-16 pb-20 sm:pb-24 text-center">
          <h1 className="font-display text-4xl sm:text-5xl text-gold mb-4">Stories</h1>
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <span className="text-gold text-lg">✦</span>
            <div className="h-px w-12 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
          </div>
          <p className="text-royal-100 text-lg italic leading-relaxed max-w-md mx-auto mb-8">
            Fairy tales for all ages — always free, always magical.
          </p>

          {/* Two Gold Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {classicsCategory && (
              <button
                onClick={() => selectCategory(String(classicsCategory.categoryid))}
                className={`px-8 py-3.5 rounded-xl text-lg font-display font-semibold transition-all border-2 min-w-[240px] ${
                  activeCategory === String(classicsCategory.categoryid)
                    ? 'bg-gold text-royal-900 border-gold shadow-lg shadow-gold/20'
                    : 'bg-transparent text-gold border-gold/60 hover:bg-gold/10 hover:border-gold'
                }`}
              >
                Classic Fairy Tales
              </button>
            )}
            {cherryCategory && (
              <button
                onClick={() => selectCategory(String(cherryCategory.categoryid))}
                className={`px-8 py-3.5 rounded-xl text-lg font-display font-semibold transition-all border-2 min-w-[240px] ${
                  activeCategory === String(cherryCategory.categoryid)
                    ? 'bg-gold text-royal-900 border-gold shadow-lg shadow-gold/20'
                    : 'bg-transparent text-gold border-gold/60 hover:bg-gold/10 hover:border-gold'
                }`}
              >
                The Cherry Fairies
              </button>
            )}
          </div>
        </div>

        {/* Wave curve */}
        <div className="absolute -bottom-px left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
            <path
              d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
              className="fill-cream dark:fill-bg-dark"
            />
          </svg>
        </div>
      </div>

      {/* ── Content area — centered, no sidebar ─────────── */}
      <div className="bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

          {/* Vintage Search Bar — centered */}
          <div className="max-w-md mx-auto mb-8">
            <VintageSearch
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search the archives…"
            />
          </div>

          {/* Stories Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-royal-300 dark:border-royal-600 border-t-gold rounded-full animate-spin" />
            </div>
          ) : filteredStories.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-4">📖</div>
              <p className="text-royal-700 dark:text-royal-100 text-lg mb-2">
                {searchQuery ? 'No tales match your search' : 'No stories here yet'}
              </p>
              <p className="text-royal-500 dark:text-royal-200 text-base italic">
                {searchQuery ? 'Try a different enchantment…' : 'Check back soon — new tales are always being written.'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light text-sm transition-colors"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
              {filteredStories.map((s) => (
                <Link
                  key={s.storyid}
                  to={storyUrl(s.slug)}
                  className="block bg-white dark:bg-royal-800 border border-royal-200 dark:border-royal-600 rounded-xl overflow-hidden hover:border-gold/50 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-gold/10"
                >
                  <div
                    className="aspect-[3/2] flex items-center justify-center"
                    style={{ background: 'linear-gradient(180deg, #283593 0%, #1a237e 60%, #0d1339 100%)' }}
                  >
                    {s.coverimageurl ? (
                      <img src={s.coverimageurl} alt={s.title} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-5xl">📖</span>
                    )}
                  </div>
                  <div className="p-4 sm:p-5 bg-white dark:bg-royal-800">
                    <span className="inline-block text-xs sm:text-sm font-semibold uppercase tracking-wider bg-gold/15 dark:bg-transparent text-gold-dark dark:text-gold px-2 py-0.5 dark:px-0 dark:py-0 rounded">
                      {s.subcategoryname || s.categoryname}
                    </span>
                    <h3 className="text-base sm:text-lg text-royal-900 dark:text-white font-display font-semibold mt-1.5 leading-snug">{s.title}</h3>
                    {s.author && <p className="text-royal-500 dark:text-royal-200 text-sm italic mt-1">by {s.author}</p>}
                    {s.excerpt && (
                      <p className="text-royal-500 dark:text-royal-300 text-sm mt-1.5 line-clamp-2 leading-relaxed">
                        {s.excerpt?.length > 120 ? s.excerpt.slice(0, 120) + '...' : s.excerpt}
                      </p>
                    )}
                    <div className="flex gap-4 mt-3 text-xs text-royal-400 dark:text-royal-300">
                      <span>{s.pagecount} pages</span>
                      <span>{s.readtime} min read</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}